import { useEffect, useState } from 'react';
import { Box } from '@mui/material';

import { QuestionLayout } from '../../components/QuestionLayout/QuestionLayout';
import { stringOrUndefined } from '../../storyblok/utils/stringOrUndefined';
import {
  useStepContext,
  useWidgetContext,
  extractZipCodeFromString,
  useLeadData,
} from '@widget/core';
import {
  Button,
  LoadingAnimatedIcon,
  LoadingAnimatedText,
  QuestionMarkTooltip,
} from '@toolbox/components';
import { useTimeslots } from '@widget/core/TimeSlotsProvider';
import {
  ButtonAndIconWrapper,
  ButtonWithLabelWrapper,
  ButtonLabel,
} from '@widget/presets/BookBrokerAppointmentChoiceStep/style';
import { Separator } from '@widget/presets/BookBrokerAppointmentChoiceStep/Separator';
import { APPOINTMENT_TYPE } from '@widget/api/types';

import type { TimeSlotsType } from '@widget/api/types';
import type * as React from 'react';
import type {
  BookBrokerAppointmentChoiceStepSchema,
  QuestionLayoutSchema,
} from '../../storyblok/schemas';
import type { Richtext } from 'storyblok-js-client';
import type { z } from 'zod';

type StepProps = z.infer<typeof BookBrokerAppointmentChoiceStepSchema>;
type QuestionLayoutProps = z.infer<typeof QuestionLayoutSchema>;

export const BookBrokerAppointmentChoiceStep: React.FC<StepProps> = ({
  name = 'bookBrokerAppointmentType',
  ...props
}) => {
  const questionLayoutProps = props.children[0] as QuestionLayoutProps;
  const widget = useWidgetContext();
  const step = useStepContext();
  const zipCode = widget.getFormData()?.zipCode as string | undefined;
  const { timeslots, timeslotsError, timezone, setTimeslots } = useTimeslots();
  const [loading, setLoading] = useState(true);
  const { leadData } = useLeadData();

  const getIsHideOnSite = (hideOnSite: { value: string; key: string }[]) => {
    const fd = widget.getFormData();
    return hideOnSite.some(({ value, key }) => {
      const values = value.split('||');
      return values.some((v: string) => v === fd[key]);
    });
  };

  useEffect(() => {
    if (widget?.leadData?.bookedSlot?.isNewAppointment === false) {
      widget.commit(props.id, { isNewAppointment: false }, props.nextStep || undefined);
    }
  }, [widget?.leadData?.bookedSlot?.isNewAppointment]);

  useEffect(() => {
    if (!widget?.leadData?.bookedSlot) {
      if (widget?.leadData?.id || zipCode) {
        const zipCodeValue = zipCode ? extractZipCodeFromString(zipCode as string) : undefined;
        widget.onRequestTimeSlots({
          leadId: widget?.leadData?.id,
          appointmentType: APPOINTMENT_TYPE.CALL,
          timezone,
          zipCode: zipCodeValue,
        });
        const isHideOnSite = props.hideOnSite ? getIsHideOnSite(props.hideOnSite) : false;
        isHideOnSite
          ? setTimeslots(
              (prevState: TimeSlotsType | null) =>
                ({
                  ...(prevState || {}),
                  [APPOINTMENT_TYPE.ON_SITE]: null,
                } as TimeSlotsType),
            )
          : widget.onRequestTimeSlots({
              leadId: widget?.leadData?.id,
              appointmentType: APPOINTMENT_TYPE.ON_SITE,
              timezone,
              zipCode: zipCodeValue,
            });
      }
    }
  }, [widget?.leadData?.id, props.hideOnSite, zipCode]);

  const handleSubmit = async (type: string) => {
    widget.commit(props.id, { [name as string]: type }, props.nextStep || undefined);
  };

  useEffect(() => {
    if (
      (timeslots?.[APPOINTMENT_TYPE.ON_SITE] === null ||
        timeslots?.[APPOINTMENT_TYPE.ON_SITE]?.totalOptimalSlots === 0) &&
      timeslots?.[APPOINTMENT_TYPE.CALL]?.total
    ) {
      // if there are only phone timeslots
      handleSubmit(APPOINTMENT_TYPE.CALL);
    }
    if (
      timeslots?.[APPOINTMENT_TYPE.CALL] === null &&
      timeslots?.[APPOINTMENT_TYPE.ON_SITE]?.total
    ) {
      // if there are only onsite timeslots
      handleSubmit(APPOINTMENT_TYPE.ON_SITE);
    }
    if (
      timeslotsError ||
      (timeslots?.[APPOINTMENT_TYPE.CALL] === null &&
        timeslots?.[APPOINTMENT_TYPE.ON_SITE] === null)
    ) {
      // skip step if there are no timeslots or error
      if (props.SlotsAvailableFlagFieldName) {
        widget.commit(
          props.id,
          { [props.SlotsAvailableFlagFieldName]: true },
          props.nextStep || undefined,
        );
      } else {
        step.submit(props.nextStep || undefined);
      }
    }
    if (leadData?.eventId) {
      // skip step if slot was already booked
      step.submit(props.nextStep || undefined);
    }
    if (timeslots?.[APPOINTMENT_TYPE.ON_SITE] && timeslots?.[APPOINTMENT_TYPE.CALL]) {
      setLoading(false);
    }
  }, [timeslots, timeslotsError]);

  return (
    <QuestionLayout
      key={questionLayoutProps._uid}
      back={questionLayoutProps.back}
      submit={questionLayoutProps.submit}
      skip={questionLayoutProps.skip}
      title={questionLayoutProps.title}
      description={questionLayoutProps.description}
      backButtonText={stringOrUndefined(questionLayoutProps.backButtonText)}
      closeButtonText={stringOrUndefined(questionLayoutProps.closeButtonText)}
      submitButtonText={stringOrUndefined(questionLayoutProps.submitButtonText)}
      skipButtonText={stringOrUndefined(questionLayoutProps.skipButtonText)}
      socialProofMessage={props.socialProofMessage as Richtext}
      maxW={!questionLayoutProps.maxW ? undefined : questionLayoutProps.maxW}
      headerType={props.headerType}
    >
      {!loading ? (
        <Box
          mt={1}
          mb={4}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        >
          {!!timeslots?.[APPOINTMENT_TYPE.ON_SITE] &&
            timeslots[APPOINTMENT_TYPE.ON_SITE]!.totalOptimalSlots > 0 && (
              <ButtonWithLabelWrapper>
                {!!props.onsiteButtonHighlightLabel && (
                  <ButtonLabel>{props.onsiteButtonHighlightLabel}</ButtonLabel>
                )}
                <ButtonAndIconWrapper>
                  <Button
                    onClick={() => handleSubmit(APPOINTMENT_TYPE.ON_SITE)}
                    variant="secondary"
                    className={`cta-button ${
                      props.onsiteButtonHighlightLabel ? 'hightlighted' : ''
                    }`}
                    size="large"
                  >
                    {props.onsiteButtonLabel}
                  </Button>
                  {props.onsiteButtonInfo && <QuestionMarkTooltip text={props.onsiteButtonInfo} />}
                </ButtonAndIconWrapper>
              </ButtonWithLabelWrapper>
            )}
          <Separator />
          {!!timeslots?.[APPOINTMENT_TYPE.CALL] && timeslots[APPOINTMENT_TYPE.CALL]!.total > 0 && (
            <ButtonWithLabelWrapper>
              {!!props.callButtonHighlightLabel && (
                <ButtonLabel>{props.callButtonHighlightLabel}</ButtonLabel>
              )}
              <ButtonAndIconWrapper>
                <Button
                  onClick={() => handleSubmit(APPOINTMENT_TYPE.CALL)}
                  variant="secondary"
                  size="large"
                  className={`cta-button ${props.callButtonHighlightLabel ? 'hightlighted' : ''}`}
                >
                  {props.callButtonLabel}
                </Button>
                {props.callButtonInfo && <QuestionMarkTooltip text={props.callButtonInfo} />}
              </ButtonAndIconWrapper>
            </ButtonWithLabelWrapper>
          )}
        </Box>
      ) : (
        <Box css={{ display: 'flex', justifyContent: 'space-around', width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <LoadingAnimatedIcon />
            <Box mt={3}>
              <LoadingAnimatedText text={props.loadingText} />
            </Box>
          </Box>
        </Box>
      )}
    </QuestionLayout>
  );
};
